import React, { useState, useMemo } from 'react'
import { ChevronLeft, ChevronRight } from '@mui/icons-material'
import ImageWithApiKey from './ImageWithApiKey'
import { API_BASE_URL, X_API_KEY } from '../../services/api'

interface GalleryItem {
  id: number
  fileUrl: string
  fileName: string
  cover?: boolean
  status: number
}

interface GalleryProps {
  gallery: GalleryItem[]
  coverImage?: GalleryItem
  gender?: string | null
  isEstablishment?: boolean
  isHovered?: boolean
  statusToShow: number[]
  customDefaultImage?: string
}

const GalleryCards: React.FC<GalleryProps> = ({
  gallery,
  gender,
  statusToShow,
  isEstablishment = false,
  isHovered = false,
  customDefaultImage,
}) => {
  const [currentIndex, setCurrentIndex] = useState(0)

  const defaultImageUrl = () => {
    if (customDefaultImage) return customDefaultImage

    const baseUrl = window.location.origin
    if (isEstablishment) return `${baseUrl}/images/establishment.jpg`

    const genderImages: { [key: string]: string } = {
      man: `${baseUrl}/images/silouhette-male.jpg`,
      woman: `${baseUrl}/images/silouhette-female.jpg`,
      transgender: `${baseUrl}/images/silouhette-transgender.jpg`,
      couple: `${baseUrl}/images/silouhette-couple.jpg`,
    }

    return genderImages[gender || ''] || `${baseUrl}/images/silouhette.jpg`
  }

  const filteredGallery = useMemo(
    () =>
      gallery.filter(item =>
        statusToShow.some(status =>
          item.status.toString().includes(status.toString()),
        ),
      ),
    [gallery, statusToShow],
  )

  const sortedGallery = useMemo(() => {
    return filteredGallery.length
      ? [...filteredGallery].sort(item => (item.cover ? -1 : 1))
      : [
          {
            id: 0,
            fileUrl: defaultImageUrl(),
            fileName: 'Default Image',
            status: 0,
          },
        ]
  }, [filteredGallery])

  const handlePrev = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setCurrentIndex(
      prev => (prev - 1 + sortedGallery.length) % sortedGallery.length,
    )
  }

  const handleNext = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setCurrentIndex(prev => (prev + 1) % sortedGallery.length)
  }

  const currentImage = sortedGallery[currentIndex]

  const getImageUrl = (item: GalleryItem) => {
    if (item.fileUrl) return item.fileUrl
    if (item.fileName) {
      return `${API_BASE_URL}/images/${item.id}/${item.fileName}`
    }
    return defaultImageUrl()
  }

  return (
    <div className='relative w-full h-full overflow-hidden'>
      <div
        className='w-full h-full transition-transform duration-300 ease-out'
        style={{ transform: isHovered ? 'scale(1.1)' : 'scale(1)' }}
      >
        <ImageWithApiKey
          imageUrl={getImageUrl(currentImage)}
          apiKey={X_API_KEY}
          height='100%'
          onError={e => (e.currentTarget.src = defaultImageUrl())}
        />
      </div>

      {sortedGallery.length > 1 && (
        <>
          <button
            onClick={handlePrev}
            className='absolute left-0 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 text-white p-2 rounded-r z-10'
          >
            <ChevronLeft />
          </button>
          <button
            onClick={handleNext}
            className='absolute right-0 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 text-white p-2 rounded-l z-10'
          >
            <ChevronRight />
          </button>
          <div className='absolute bottom-0 left-1/2 transform -translate-x-1/2 bg-black bg-opacity-50 text-white px-4 py-1 text-sm rounded-t z-10'>
            {currentIndex + 1}/{sortedGallery.length}
          </div>
        </>
      )}
    </div>
  )
}

export default GalleryCards
